/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing quick list modal for email configs.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuthStore } from '../../../store/auth';
import { fetchEmailConfigs } from '../api/emailSettingsApi';
import type { EmailConfig } from '../types';

export function useQuickList() {
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || 'main';

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListConfigs, setQuickListConfigs] = useState<EmailConfig[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      // Load first page
      const firstPage = await fetchEmailConfigs(
        {
          page: 1,
          limit: 100,
          sortBy: 'name',
          sortOrder: 'asc',
        },
        tenantSlug,
        user?.id ? Number(user.id) : undefined
      );

      let allConfigs = [...firstPage.data];

      // If we got 100 results, try to load more pages
      if (firstPage.data.length === 100 && firstPage.pagination.pages > 1) {
        const totalPages = Math.min(firstPage.pagination.pages, 10); // Limit to 10 pages max (1000 configs)
        for (let page = 2; page <= totalPages; page++) {
          const nextPage = await fetchEmailConfigs(
            {
              page,
              limit: 100,
              sortBy: 'name',
              sortOrder: 'asc',
            },
            tenantSlug,
            user?.id ? Number(user.id) : undefined
          );
          allConfigs = [...allConfigs, ...nextPage.data];
        }
      }

      setQuickListConfigs(allConfigs);
    } catch {
      setQuickListConfigs([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [tenantSlug, user?.id]);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListConfigs.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, quickListConfigs.length, loadQuickList]);

  // Filter configs client-side
  const filteredQuickList = useMemo(() => {
    const configs = quickListConfigs.filter((c) => c.id != null);
    if (!quickListSearch.trim()) {
      return configs;
    }
    const searchLower = quickListSearch.toLowerCase();
    return configs.filter(
      (config) =>
        config.name?.toLowerCase().includes(searchLower) ||
        config.host?.toLowerCase().includes(searchLower) ||
        config.fromEmail?.toLowerCase().includes(searchLower) ||
        String(config.port || '').includes(searchLower)
    );
  }, [quickListConfigs, quickListSearch]);

  const openQuickList = useCallback(() => {
    setShowQuickList(true);
  }, []);

  const closeQuickList = useCallback(() => {
    setShowQuickList(false);
    setQuickListSearch('');
  }, []);

  return {
    showQuickList,
    quickListSearch,
    filteredQuickList,
    isQuickListLoading,
    openQuickList,
    closeQuickList,
    setQuickListSearch,
  };
}

